import React, { useState, useEffect } from "react";

import { NotificationManager } from "react-notifications";
import { useHistory, useParams } from "react-router-dom";
import CommonLoader from "../common/CommonLoader";
import PageHeading from "../common/PageHeading";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  SaveWaterProfile,
  UpdateWaterProfile,
  GetWaterProfileById,
  CheckTypeAvailability, // New import for type availability checking
} from "../../services/ClientWaterProfileService";

const AddWaterProfile = ({accountNumber}) => {
  const history = useHistory();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    accountNumber: "",
    type: "",
    calcium: null,
    totalAlkalinity: null,
    bicarbonate: null,
    sulfate: null,
    magnesium: null,
    totalHardness: null,
    iron: null,
    pH: null,
    totalChlorine: null,
    residualAlkalinity: null,
    chloride: null,
    sulfateToChlorideRatio: null,
    sodium: null,
    zinc: null,
    potassium: null,
    copper: null,
    tds: null,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [checkingType, setCheckingType] = useState(false);
  const [typeValidationStatus, setTypeValidationStatus] = useState(null); // 'available', 'unavailable', or null

  useEffect(() => {    
    if (id && id!=="add") {
      fetchProfileData(parseInt(id, 10));
    }
    const value = accountNumber;
    setFormData((prev) => ({ ...prev, accountNumber: value }));
  }, [id]);

  useEffect(() => {
    const value = accountNumber;
    setFormData((prev) => ({ ...prev, accountNumber: value }));
  }, [accountNumber]);

  // New useEffect for type validation
  useEffect(() => {    
    if(String(formData.type)===""){
                setTypeValidationStatus(null);
              return
            }
    const checkTypeAvailability = async () => {
      if (id === "add" && accountNumber && formData.type.trim()) {
        setCheckingType(true);
        setTypeValidationStatus(null);
        
        try {
          // Add delay before API call
          const timeoutId = setTimeout(async () => {
            try {               
              const response = await CheckTypeAvailability({
                accountNumber: String(formData.accountNumber),
                type: String(formData.type),
              });
              
              if (response && response.statusCode === 200 && response.results === true) {
                setTypeValidationStatus('available');
                setErrors(prev => ({ ...prev, type: "" }));
              } else {
                setTypeValidationStatus('unavailable');
                setErrors(prev => ({ 
                  ...prev, 
                  type: "This type/water profile name is not available for this client. Please try a different name." 
                }));
              }
            } catch (error) {
              setTypeValidationStatus('unavailable');
              setErrors(prev => ({ 
                ...prev, 
                type: "Error checking type availability. Please try a different name." 
              }));
            } finally {
              setCheckingType(false);
            }
          }, 500); // 500ms delay

          return () => clearTimeout(timeoutId);
        } catch (error) {
          setCheckingType(false);
        }
      } else if (!accountNumber || !formData.type.trim()) {
        setTypeValidationStatus(null);
        setCheckingType(false);
      }
    };

    checkTypeAvailability();
  }, [accountNumber, formData.type, id]);

  const fetchProfileData = async (profileId) => {
    setLoading(true);
    try {      
      const response = await GetWaterProfileById(profileId);      
      if (response && response.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to fetch profile data");
      }
    } catch (error) {
      NotificationManager.error("Error fetching profile data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.accountNumber)
      newErrors.accountNumber = "Account number is required";
    if (!formData.type) newErrors.type = "Type is required";
    
    // Don't allow saving if type is not available
    if (id === "add" && typeValidationStatus === 'unavailable') {
      newErrors.type = "This type/water profile name is not available for this client. Please try a different name.";
    }

    const numericFields = [
      "calcium",
      "totalAlkalinity",
      "bicarbonate",
      "sulfate",
      "magnesium",
      "totalHardness",
      "iron",
      "pH",
      "totalChlorine",
      "residualAlkalinity",
      "chloride",
      "sulfateToChlorideRatio",
      "sodium",
      "zinc",
      "potassium",
      "copper",
      "tds",
    ];

    numericFields.forEach((field) => {
      if (formData[field] && isNaN(formData[field])) {
        newErrors[field] = "Must be a valid number";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Additional check to prevent saving if type is still being validated or unavailable
    if (id === "add" && (checkingType || typeValidationStatus === 'unavailable')) {
      NotificationManager.warning("Please wait for type validation to complete or choose a different type name.");
      return;
    }

    const profileData = {
      ...formData,
      accountNumber: String(formData.accountNumber || ""),
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id!=="add") {
        await UpdateWaterProfile(profileData);
        NotificationManager.success("Profile updated successfully");
      } else {
        await SaveWaterProfile(profileData);
        NotificationManager.success("Profile added successfully");
      }
      history.push("/water-profiles");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === "" ? null : Number(value) || value,
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: value,
    }));
    
    // Reset validation status when user types
    if (value.trim()) {
      setTypeValidationStatus(null);
    }
  };

  const handleCancel = () => {
    history.push("/water-profiles");
  };

  // Helper function to get type field status
  const getTypeFieldStatus = () => {
    if (checkingType) return { color: 'info', text: 'Checking availability...' };
    if (typeValidationStatus === 'available') return { color: 'success', text: 'Type name is available' };
    if (typeValidationStatus === 'unavailable') return { color: 'danger', text: errors.type };
    return null;
  };

  const typeStatus = getTypeFieldStatus();

  const CHEMICAL_FIELDS = [
    { field: "calcium", label: "Calcium" },
    { field: "totalAlkalinity", label: "Total Alkalinity" },
    { field: "bicarbonate", label: "Bicarbonate" },
    { field: "sulfate", label: "Sulfate" },
    { field: "magnesium", label: "Magnesium" },
    { field: "totalHardness", label: "Total Hardness" },
    { field: "iron", label: "Iron" },
    { field: "pH", label: "pH" },
    { field: "totalChlorine", label: "Total Chlorine" },
    { field: "residualAlkalinity", label: "Residual Alkalinity" },
    { field: "chloride", label: "Chloride" },
    { field: "sulfateToChlorideRatio", label: "Sulfate to Chloride Ratio" },
    { field: "sodium", label: "Sodium" },
    { field: "zinc", label: "Zinc" },
    { field: "potassium", label: "Potassium" },
    { field: "copper", label: "Copper" },
    { field: "tds", label: "TDS" },
  ];

  const isEdit = id && id !== "add";

  return (
    <Box sx={{ p: 2 }}>
      <PageHeading
        title={isEdit ? "Edit Water Profile" : "Add New Water Profile"}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2">
              <strong>Account Number:</strong> {formData.accountNumber}
            </Typography>
          </Grid>

          {isEdit ? (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2">
                <strong>Type:</strong> {formData.type}
              </Typography>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Water Profile Type *"
                fullWidth
                size="small"
                value={formData.type}
                onChange={handleTypeChange}
                placeholder="Enter water profile type"
                disabled={!accountNumber}
                error={Boolean(errors.type)}
                helperText={errors.type || typeStatus?.text || " "}
                InputProps={{
                  endAdornment: checkingType ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
          )}
        </Grid>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
          Chemical Properties (mg/L)
        </Typography>

        {/* Six controls per row on md and up. */}
        <Grid container spacing={2}>
          {CHEMICAL_FIELDS.map(({ field, label }) => (
            <Grid size={{ xs: 12, sm: 6, md: 2 }} key={field}>
              <TextField
                label={label}
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: "0.01" }}
                value={formData[field] ?? ""}
                onChange={handleChange(field)}
                error={Boolean(errors[field])}
                helperText={errors[field] || " "}
              />
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={handleCancel} type="button">
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            type="button"
            disabled={
              id === "add" &&
              (checkingType || typeValidationStatus === "unavailable")
            }
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </Stack>
      </Paper>

      <CommonLoader loading={loading} />
    </Box>
  );
};

export default AddWaterProfile;
